import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Loader2, Send } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin toggle: pause / resume WhatsApp notifications (CallMeBot)
export const WhatsAppToggle = () => {
    const { getAuthHeaders } = useAuth();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/admin/settings/whatsapp`, { headers: getAuthHeaders() });
            setStatus(r.data);
        } catch { /* sidebar: fail silently */ }
    }, [getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (enabled) => {
        setBusy(true);
        try {
            const r = await axios.put(`${API}/api/admin/settings/whatsapp`, { enabled }, { headers: getAuthHeaders() });
            setStatus(r.data);
            toast.success(enabled ? 'Notificaciones WhatsApp activadas' : 'Notificaciones WhatsApp pausadas');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo cambiar');
        } finally {
            setBusy(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            await axios.post(`${API}/api/admin/settings/whatsapp/test`, {}, { headers: getAuthHeaders() });
            toast.success('Mensaje de prueba enviado a tu WhatsApp');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo enviar la prueba');
        } finally {
            setTesting(false);
        }
    };

    if (!status) return null;
    const on = status.enabled;

    return (
        <div className="px-4 pb-2" data-testid="whatsapp-toggle-section">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${on ? 'bg-green-50 border-green-200' : 'bg-slate-100 border-slate-200'}`}>
                <MessageCircle className={`w-4 h-4 shrink-0 ${on ? 'text-green-600' : 'text-slate-400'}`} />
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${on ? 'text-green-800' : 'text-slate-600'}`}>WhatsApp admin</p>
                    <p className="text-[11px] text-slate-500 truncate" data-testid="whatsapp-status-text">
                        {!status.configured ? 'No configurado' : on ? `Activas · ${status.phone_masked || ''}` : 'Pausadas'}
                    </p>
                </div>
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
                    <Switch checked={on} onCheckedChange={toggle} disabled={!status.configured} aria-label="Activar o pausar notificaciones WhatsApp" data-testid="whatsapp-toggle-switch" />
                )}
            </div>
            {status.configured && (
                <button
                    type="button"
                    onClick={sendTest}
                    disabled={testing}
                    className="mt-1.5 w-full inline-flex items-center justify-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 disabled:opacity-50"
                    data-testid="whatsapp-test-btn"
                >
                    {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Enviar prueba
                </button>
            )}
        </div>
    );
};

export default WhatsAppToggle;
